import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
} from "react"
import * as z from "zod"
import GuestCheckoutSummary from "./GuestCheckoutSummary"
import Loader from "../ui/loader"
import Recaptcha from "../auth/register-form/Recaptcha"
import secureLocalStorage from "react-secure-storage"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { LoginFormContext } from "../auth/login-form/context"
import { SvgWarning } from "../icons"
import { UserCtx } from "../../context/userContext"
import { UserExists } from "../../api/auth.service"
import { useToast } from "../../hooks/useToast"
import { EmailLogin } from "../../api/auth.service"
import { getUserStats } from "../../utils/getUserStats"
import { isLocalStorageAvailable } from "../../utils/isLocalStorageAvailable"
import { ContactRequests } from "../../api/app.service"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { useParams } from "react-router-dom"

interface Props {
  coachPublicProfileData: any
  service: any
  guestEmail: any
  sentVerification: any
  cardFieldErrors: {
    cardName: boolean
    cardNumber: boolean
    cardExpiry: boolean
    cardCvv: boolean
  }
  setCardFieldErrors: React.Dispatch<
    React.SetStateAction<{
      cardName: boolean
      cardNumber: boolean
      cardExpiry: boolean
      cardCvv: boolean
    }>
  >
}

const ServiceGuestLogin: React.FC<Props> = ({
  coachPublicProfileData,
  service,
  guestEmail,
  sentVerification,
  cardFieldErrors,
  setCardFieldErrors,
}) => {
  const { toast } = useToast()
  const { user } = useContext(UserCtx)
  const { getUser } = useContext(CommonFunctionCtx)
  const { endpoint_slug: endpointSlug } = useParams<{
    endpoint_slug: string
  }>()

  const {
    email,
    setEmail,
    password,
    setPassword,
    setView,
    getSetupInfo,
    updateTimezone,
    onSuccess,
  } = useContext(LoginFormContext)
  const [loadingAvailablePeriods, setLoadingAvailablePeriods] =
    useState<boolean>(false)
  const [isOwner, setIsOwner] = useState<boolean>()
  const [activeContact, setActiveContact] = useState<any>(null)
  const [cardValid, setCardValid] = useState(false)
  const [isNotRobot, setIsNotRobot] = useState<boolean>(false)
  const [connectionCompleted, setConnectionCompleted] = useState<boolean>(false)
  const [leftHeight, setLeftHeight] = useState<number | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [errors, setErrors] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confUserPassword: "",
  })

  useLayoutEffect(() => {
    if (leftContainerRef.current) {
      setLeftHeight(leftContainerRef.current.offsetHeight)
    }
  }, [loadingAvailablePeriods])

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const leftContainerRef = useRef<HTMLDivElement | null>(null)
  const freeService =
    service.service_details.type !== "consultation" &&
    service.service_details.billing.amount === "0"
  const paidService = service.service_details.billing.amount !== "0"

  const [emailError, setEmailError] = useState<string | undefined>()
  const [authError, setAuthError] = useState<string | undefined>()
  const validateEmail = (email: string) => z.string().email().safeParse(email)
  const validatePassword = (password: string) =>
    z
      .string()
      .min(8, "Invalid password")
      .regex(/^(?=.*[0-9])(?=.*\W).{8,}$/, {
        message: "Incorrect Password. Please try again",
      })
      .safeParse(password)

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLoading(true)
    setEmailError(undefined)
    setAuthError(undefined)

    try {
      const emailCheck = validateEmail(email)
      if (!emailCheck.success) {
        setEmailError(emailCheck.error.issues[0].message)
        return
      }

      const passwordCheck = validatePassword(password)
      if (!passwordCheck.success) {
        setAuthError(passwordCheck.error.issues[0].message)
        return
      }

      const emailExists = await doesEmailExist(email)
      if (!emailExists) {
        setEmailError("Email not found")
        return
      }

      setView("password")

      secureLocalStorage.setItem(
        "coachDetails",
        JSON.stringify(coachPublicProfileData)
      )

      await login()
    } catch (err: any) {
      setAuthError("An unexpected error occurred")
      console.error(err)
    }
  }

  const login = async () => {
    try {
      const data = await EmailLogin.loginEmail({
        email,
        password,
        stats: getUserStats(),
      })

      if (!data) throw new Error("No data returned")

      const storedData = JSON.stringify(data)
      if (isLocalStorageAvailable()) {
        secureLocalStorage.setItem("user", storedData)
      } else {
        toast.error("Local storage is not available")
      }

      await Promise.all([
        getSetupInfo(data.user_id),
        updateTimezone(data.user_id, data.access_token),
      ])

      const user = await getUser()
      onSuccess(user)

      const rawCoachDetails = secureLocalStorage.getItem("coachDetails")
      const coachDetails =
        typeof rawCoachDetails === "string" ? JSON.parse(rawCoachDetails) : null

      if (coachDetails) {
        let foundContact: any = null
        try {
          const contactData = await ContactRequests.getContactList({
            profile_id: user.activeProfileId,
            profile_type: user.activeProfile,
          })
          foundContact = contactData.contact_list?.find(
            (contact: any) => contact.endpoint_slug === endpointSlug
          )
        } catch (ex) {
          console.error("Error fetching contact list:", ex)
        }

        if (!foundContact) {
          try {
            await ContactRequests.createContactConnection({
              sender_profile_id: coachDetails.coach_profile_id,
              receiver_profile_id: user.activeProfileId,
              user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
              status: user.isCoach ? "c2c" : "connected",
            })
            const contactData = await ContactRequests.getContactList({
              profile_id: user.activeProfileId,
              profile_type: user.activeProfile,
            })
            foundContact = contactData.contact_list?.find(
              (contact: any) => contact.endpoint_slug === endpointSlug
            )
          } catch (ex) {
            console.error("Failed to create contact connection:", ex)
          }
        }

        setActiveContact(foundContact)
        setConnectionCompleted(true)
        secureLocalStorage.removeItem("coachDetails")
      }

      return true
    } catch (error: any) {
      console.error(error)
      setAuthError(error.response?.data?.message || "Invalid email or password")
      return false
    }
  }

  const canSubmit = !!email?.trim() && !!password?.trim() && !!isNotRobot

  const doesEmailExist = async (email: string) => {
    try {
      const data = await UserExists.verifyUserExists({
        attributes_to_verify: {
          email,
        },
      })

      return { found: data.user_found }
    } catch (error) {
      console.error(error)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)

    const emailSchema = z.string().email().safeParse(value)
    if (!emailSchema.success) {
      return
    }
  }

  useEffect(() => {
    if (String(coachPublicProfileData?.user_id) === user?.user_id) {
      setIsOwner(true)
    } else {
      setIsOwner(false)
    }
  }, [coachPublicProfileData, user])

  const initialEmailSet = useRef(false)

  useEffect(() => {
    if (!initialEmailSet.current && guestEmail) {
      setEmail(guestEmail)
      initialEmailSet.current = true
    }
  }, [guestEmail, setEmail])

  useEffect(() => {
    if (!email) return

    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    debounceTimer.current = setTimeout(async () => {
      try {
        const result = await doesEmailExist(email)
        sentVerification(result?.found || false)
      } catch (err) {
        console.error(err)
      }
    }, 500)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [email])

  return (
    <div className="flex flex-col items-center w-full mb-4">
      {" "}
      <div className="gap-4 md:gap-20 mb-4 w-full p-0">
        <div className="flex flex-col gap-[12px] px-[20px] md:col-span-2 justify-center items-center w-full">
          <div className="w-full font-bold">Enter your information below:</div>

          <div className="flex flex-col w-[100%]">
            <Input
              label="Email"
              id="email"
              placeholder="Email Address"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              style={{
                backgroundColor: errors.email ? "rgba(255, 0, 0, 0.05)" : "",
              }}
            />
            {errors.email && (
              <div className="flex items-center text-red-500 text-sm mt-1">
                <SvgWarning className="w-[18px] h-[18px] mr-1" />
                {errors.email}
              </div>
            )}
          </div>

          <div className="flex gap-4 w-[100%]">
            <div className="flex flex-col w-full">
              <Input
                label="Password"
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="border-grayCloud focus:border-blurple hover:bg-grayFlash hover:border-grayFlash h-14 cursor-text rounded-md pl-4 w-full border-2 focus:outline-none"
              />
              {errors.password && (
                <div className="flex items-center text-red-500 text-sm mt-1">
                  <SvgWarning className="w-[18px] h-[18px] mr-1" />
                  {errors.password}
                </div>
              )}
            </div>
          </div>
          <div className="w-full">
            <GuestCheckoutSummary
              coachPublicProfileData={coachPublicProfileData}
              service={service}
              connectionCompleted={connectionCompleted}
              setLoading={setLoading}
              cardFieldErrors={cardFieldErrors}
              setCardFieldErrors={setCardFieldErrors}
            />
          </div>

          <div className="mt-2 w-full">
            <div className="mb-4">
              <Recaptcha setIsNotRobot={setIsNotRobot} />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              className="w-full text-md ml-2"
            >
              {loading ? <Loader /> : "Continue"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServiceGuestLogin
